"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, MessageSquare, Send, Copy, Check, ExternalLink, ShieldAlert, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CustomSelect } from "@/components/ui/custom-select";
import { db } from "@/lib/firebase";
import { doc, setDoc, arrayUnion, serverTimestamp } from "firebase/firestore";

const CONTACT_CATEGORIES = [
  "General Inquiry",
  "College Cutoff Data Issue",
  "Predictor Bug Report",
  "Feature Request / Proposal",
  "Partnership & Collaboration",
  "Technical Support",
  "Other Support"
];

export function ContactContent() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    category: "",
    subject: "",
    message: "",
  });

  const [copied, setCopied] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const contactEmail = "py.assistance@hotmail.com";

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(contactEmail);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy email", err);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleCategoryChange = (val: string) => {
    setFormData((prev) => ({ ...prev, category: val }));
    if (errors.category) {
      setErrors((prev) => ({ ...prev, category: "" }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }
    if (!formData.category) newErrors.category = "Category is required";
    if (!formData.subject.trim()) newErrors.subject = "Subject is required";
    if (!formData.message.trim()) newErrors.message = "Message is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      const emailId = formData.email.trim().toLowerCase();

      await setDoc(doc(db, "feedbacks", emailId), {
        email: emailId,
        submissions: arrayUnion({
          name: formData.name.trim(),
          category: formData.category,
          subject: formData.subject.trim(),
          message: formData.message.trim(),
          createdAt: new Date().toISOString(),
          type: "contact"
        }),
        lastUpdatedAt: serverTimestamp()
      }, { merge: true });

      setIsSubmitting(false);
      setSubmitted(true);
    } catch (error) {
      console.error("Error submitting contact form:", error);
      setIsSubmitting(false);
      setErrors({ submit: "Failed to send message. Please try again later or email us directly." });
    }
  };

  const handleLaunchMailto = () => {
    const subjectLine = encodeURIComponent(`[CCMT Finder Inquiry] [${formData.category}] ${formData.subject}`);
    const bodyText = encodeURIComponent(
      `Hi CCMT Finder Team,\n\n${formData.message}\n\nBest Regards,\n${formData.name}\nEmail: ${formData.email}`
    );
    window.location.href = `mailto:${contactEmail}?subject=${subjectLine}&body=${bodyText}`;
  };

  return (
    <div className="min-h-screen bg-background selection:bg-primary/30 pt-10 pb-20 overflow-hidden">
      {/* Glow backgrounds */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full -z-10 pointer-events-none">
        <div className="absolute top-[-5%] left-[-15%] w-[60%] h-[40%] bg-primary/10 blur-[130px] rounded-full opacity-60" />
        <div className="absolute bottom-[20%] right-[-15%] w-[60%] h-[40%] bg-secondary/10 blur-[130px] rounded-full opacity-60" />
      </div>

      <div className="container mx-auto px-6 max-w-6xl">
        {/* Hero Section */}
        <section className="text-center py-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-surface-container/50 border border-outline-variant/10 text-primary text-sm font-bold mb-6 shadow-sm backdrop-blur-md"
          >
            <MessageSquare className="size-4" />
            <span>Connect With Us</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-3xl md:text-5xl font-display font-black tracking-tight text-foreground mb-6 leading-tight"
          >
            Get In Touch
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-base md:text-lg text-on-surface-variant max-w-2xl mx-auto leading-relaxed"
          >
            Have a question, feedback, feature proposal, or bug report? Feel free to contact our team. We would love to hear from you.
          </motion.p>
        </section>

        {/* Contact Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-8 items-start">
          {/* Left Column: Cards */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="lg:col-span-5 space-y-6"
          >
            {/* Email Contact Card */}
            <div className="p-8 rounded-3xl bg-surface-container border border-outline-variant/20 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 right-0 p-6 text-primary/5 pointer-events-none">
                <Mail className="size-36 -mr-10 -mt-10" />
              </div>

              <div className="flex items-center gap-3 text-primary mb-4">
                <Mail className="size-6" />
                <h3 className="font-display font-bold text-lg text-foreground">Direct Email</h3>
              </div>

              <p className="text-on-surface-variant text-sm mb-6 leading-relaxed">
                Send your queries or proposals directly to our primary support and assistance email address.
              </p>

              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between p-4 rounded-2xl bg-surface-container-low border border-outline-variant/15 text-sm md:text-base font-semibold text-foreground select-all break-all">
                  <span>{contactEmail}</span>
                  <button
                    onClick={handleCopyEmail}
                    className="ml-3 flex-shrink-0 size-8 rounded-lg bg-surface-container-high hover:bg-surface-tint/20 text-on-surface-variant hover:text-primary flex items-center justify-center transition-colors active:scale-95"
                    title="Copy Email"
                  >
                    {copied ? <Check className="size-4 text-emerald-500" /> : <Copy className="size-4" />}
                  </button>
                </div>
                <a
                  href={`mailto:${contactEmail}`}
                  className="w-full inline-flex items-center justify-center gap-2 h-11 px-4 rounded-xl border border-primary/20 text-primary font-bold text-sm hover:bg-primary/5 transition-all"
                >
                  Open in Mail Client
                  <ExternalLink className="size-3.5" />
                </a>
              </div>
            </div>



            {/* Disclamer mini card */}
            <div className="p-6 rounded-3xl bg-amber-500/5 border border-amber-500/20 shadow-sm flex items-start gap-4">
              <ShieldAlert className="size-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-display font-bold text-sm text-foreground">Official CCMT Note</h4>
                <p className="text-on-surface-variant text-xs mt-1 leading-relaxed">
                  For official counseling dates, seat allocations, candidate credentials updates, and fees payments, always use the government&apos;s authorized CCMT portal.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Interactive Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="lg:col-span-7"
          >
            <div className="p-8 rounded-3xl bg-surface-container border border-outline-variant/20 shadow-sm relative">
              <AnimatePresence mode="wait">
                {!submitted ? (
                  <motion.form
                    key="contact-form"
                    onSubmit={handleSubmit}
                    className="space-y-5"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Name */}
                      <div className="space-y-1.5">
                        <label htmlFor="name" className="text-xs font-bold text-foreground">
                          Your Name
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className={`flex h-11 w-full rounded-xl border bg-surface-container-low px-4 text-sm text-foreground placeholder:text-on-surface-variant/40 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all ${
                            errors.name ? "border-destructive/60 focus:ring-destructive/20" : "border-outline-variant/20"
                          }`}
                          placeholder="Piyush Prajapati"
                        />
                        {errors.name && (
                          <p className="text-destructive text-xs font-semibold flex items-center gap-1">
                            <AlertCircle className="w-3.5 h-3.5" /> {errors.name}
                          </p>
                        )}
                      </div>

                      {/* Email */}
                      <div className="space-y-1.5">
                        <label htmlFor="email" className="text-xs font-bold text-foreground">
                          Email Address
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className={`flex h-11 w-full rounded-xl border bg-surface-container-low px-4 text-sm text-foreground placeholder:text-on-surface-variant/40 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all ${
                            errors.email ? "border-destructive/60 focus:ring-destructive/20" : "border-outline-variant/20"
                          }`}
                          placeholder="example@mail.com"
                        />
                        {errors.email && (
                          <p className="text-destructive text-xs font-semibold flex items-center gap-1">
                            <AlertCircle className="w-3.5 h-3.5" /> {errors.email}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Category */}
                    <div className="space-y-1.5 relative z-50">
                      <label htmlFor="category" className="text-xs font-bold text-foreground">
                        Category
                      </label>
                      <CustomSelect
                        value={formData.category}
                        onChange={handleCategoryChange}
                        options={CONTACT_CATEGORIES}
                        placeholder="Select a category"
                        className={errors.category ? "[&>button]:border-destructive [&>button]:focus:ring-destructive/20" : ""}
                      />
                      {errors.category && (
                        <p className="text-destructive text-xs font-semibold flex items-center gap-1">
                          <AlertCircle className="w-3.5 h-3.5" /> {errors.category}
                        </p>
                      )}
                    </div>

                    {/* Subject */}
                    <div className="space-y-1.5 z-10 relative">
                      <label htmlFor="subject" className="text-xs font-bold text-foreground">
                        Subject
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        className={`flex h-11 w-full rounded-xl border bg-surface-container-low px-4 text-sm text-foreground placeholder:text-on-surface-variant/40 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all ${
                          errors.subject ? "border-destructive/60 focus:ring-destructive/20" : "border-outline-variant/20"
                        }`}
                        placeholder="Inquiry about prediction data / Feature request"
                      />
                      {errors.subject && (
                        <p className="text-destructive text-xs font-semibold flex items-center gap-1">
                          <AlertCircle className="w-3.5 h-3.5" /> {errors.subject}
                        </p>
                      )}
                    </div>

                    {/* Message */}
                    <div className="space-y-1.5 z-10 relative">
                      <label htmlFor="message" className="text-xs font-bold text-foreground">
                        Your Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        rows={5}
                        className={`flex w-full rounded-xl border bg-surface-container-low p-4 text-sm text-foreground placeholder:text-on-surface-variant/40 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all resize-none ${
                          errors.message ? "border-destructive/60 focus:ring-destructive/20" : "border-outline-variant/20"
                        }`}
                        placeholder="Type your message here..."
                      />
                      {errors.message && (
                        <p className="text-destructive text-xs font-semibold flex items-center gap-1">
                          <AlertCircle className="w-3.5 h-3.5" /> {errors.message}
                        </p>
                      )}
                    </div>

                    {errors.submit && (
                      <div className="bg-destructive/10 text-destructive text-sm px-4 py-3 rounded-xl flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                        <p>{errors.submit}</p>
                      </div>
                    )}

                    <Button 
                      type="submit" 
                      className="w-full h-12 rounded-xl font-bold gap-2"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Sending...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          Send Message
                          <Send className="size-4" />
                        </span>
                      )}
                    </Button>
                  </motion.form>
                ) : (
                  <motion.div
                    key="success-screen"
                    className="py-12 px-4 text-center space-y-6"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <div className="size-16 rounded-full bg-emerald-500/10 text-emerald-500 flex items-center justify-center mx-auto mb-4">
                      <Check className="size-8" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-display font-black text-2xl text-foreground">Message Sent!</h3>
                      <p className="text-on-surface-variant text-sm max-w-md mx-auto leading-relaxed">
                        Thank you, <span className="font-bold text-foreground">{formData.name}</span>. Your message has been successfully saved to Firestore. We have received your inquiry and our team will get back to you shortly at <span className="font-bold text-foreground">{formData.email}</span>.
                      </p>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-sm mx-auto">
                      <Button onClick={handleLaunchMailto} className="w-full sm:flex-1 h-12 rounded-xl font-bold gap-2">
                        <ExternalLink className="size-4" />
                        Launch Mail Client
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setSubmitted(false);
                          setFormData({ name: "", email: "", category: "", subject: "", message: "" });
                        }}
                        className="w-full sm:flex-1 h-12 rounded-xl font-bold"
                      >
                        Reset Form
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
